import { message, Modal, Spin, Select } from 'antd';
import { forwardRef, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { productService } from '../../../api/services/productService';
import type { LatestPriceProduct } from '../../../types/presentation';
import { Configuration } from '../../../constants/configs';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import ProductsList from './ProductsList';
import ChartComponent, { CHART_TYPES } from '../../../components/chartComponent/ChartComponent';
import fetcher from '../../../helpers/fetcher';
import { ENVIRONMENT_CONFIG } from '../../../constants/environmentConfig';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { useNotificationContext } from '../../../contexts/NotificationContext';

interface PriceDataItem {
  id: number;
  productRefId: string;
  price: number;
  inspectionRefId: string;
  createTime: number;
  updateTime: number;
  isActive: boolean;
  reportedTime?: number;
  product_name: string;
  product_brand: string;
  establishment_name: string;
}

interface PriceQueryParams {
  productRefId: string;
  limit: number;
  sortBy: string;
  sortOrder: string;
  location?: number;
}

type MonitoredProductsProps = {
  t: (key: string, options?: Record<string, any>) => string;
};

const MonitoredProducts = forwardRef<HTMLDivElement, MonitoredProductsProps>(({ t }, ref) => {
  const [products, setProducts] = useState<LatestPriceProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    baskets: [] as Array<{ id: number; name: string }>,
    departments: [] as string[],
    municipalities: [] as string[],
    allMunicipalities: [] as Array<{ name: string; department: string }>,
  });
  const [filters, setFilters] = useState({
    basketId: undefined as number | undefined,
    department: Configuration.defaultLocation.department,
    municipality: Configuration.defaultLocation.municipality,
  });

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [chartData, setChartData] = useState<{
    categories: string[];
    series: Array<{ name: string; data: number[]; color?: string }>;
  } | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const { errorNotification } = useNotificationContext();

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const basketsData = await referenceDataSyncService.getBaskets();
        const baskets = basketsData.map((basket: { id: number; name: string }) => ({
          id: basket.id,
          name: basket.name,
        }));

        const locationsData = await referenceDataSyncService.getLocations();
        const departments = Array.from(
          new Set(
            locationsData.map((location: Record<string, unknown>) => location.district as string)
          )
        ) as string[];

        const allMunicipalities = locationsData.map((location: Record<string, unknown>) => ({
          name: location.city as string,
          department: location.district as string,
        }));

        setFilterOptions({
          baskets,
          departments,
          municipalities: [], // Will be populated based on selected department
          allMunicipalities,
        });

        const basicFoodBasketId = await referenceDataSyncService.getBasicFoodBasketId();
        if (basicFoodBasketId) {
          setFilters((prev) => ({
            ...prev,
            basketId: basicFoodBasketId,
          }));
        }
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };

    loadFilterOptions();
  }, []);

  // Set default municipality when locations are loaded
  useEffect(() => {
    if (
      filterOptions.allMunicipalities.length > 0 &&
      filters.department === Configuration.defaultLocation.department
    ) {
      const filteredMunicipalities = filterOptions.allMunicipalities
        .filter((muni) => muni.department === filters.department)
        .map((muni) => muni.name);

      if (filteredMunicipalities.includes(Configuration.defaultLocation.municipality)) {
        setFilters((prev) => ({
          ...prev,
          municipality: Configuration.defaultLocation.municipality,
        }));
      }
    }
  }, [filterOptions.allMunicipalities, filters.department]);

  // Filter municipalities based on selected department
  useEffect(() => {
    if (filters.department) {
      const filteredMunicipalities = filterOptions.allMunicipalities
        .filter((muni) => muni.department === filters.department)
        .map((muni) => muni.name);

      setFilterOptions((prev) => ({
        ...prev,
        municipalities: filteredMunicipalities,
      }));

      // If current municipality is not available in filtered list, set to default if available
      setFilters((prev) => {
        if (!filteredMunicipalities.includes(prev.municipality)) {
          if (filteredMunicipalities.includes(Configuration.defaultLocation.municipality)) {
            return {
              ...prev,
              municipality: Configuration.defaultLocation.municipality,
            };
          } else {
            return {
              ...prev,
              municipality: filteredMunicipalities[0],
            };
          }
        }
        return prev;
      });
    } else {
      // If no department selected, show all municipalities
      const allMunicipalityNames = Array.from(
        new Set(filterOptions.allMunicipalities.map((muni) => muni.name))
      );
      setFilterOptions((prev) => ({
        ...prev,
        municipalities: allMunicipalityNames,
      }));
    }
  }, [filters.department, filterOptions.allMunicipalities]);

  // Reset pagination when filters change
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  }, [filters]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);

        const queryParams: {
          page: number;
          limit: number;
          basketId?: number;
          locationId?: number;
        } = {
          page: pagination.current,
          limit: pagination.pageSize,
        };

        if (filters.basketId) {
          queryParams.basketId = filters.basketId;
        }

        if (filters.department && filters.municipality) {
          const locationId = await referenceDataSyncService.getLocationId(
            filters.department,
            filters.municipality
          );
          if (locationId) {
            queryParams.locationId = locationId;
          }
        }

        const response = await productService.getLatestPricesWithProducts(queryParams);
        setProducts(response.data || []);
        setPagination((prev) => ({
          ...prev,
          total: response.total || 0,
        }));
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [
    filters.basketId,
    filters.department,
    filters.municipality,
    pagination.current,
    pagination.pageSize,
    filterOptions.baskets,
  ]);

  const handleBasketFilter = (value: number | undefined) => {
    setFilters((prev) => ({
      ...prev,
      basketId: value,
    }));
  };

  const handleDepartmentFilter = (value: string | undefined) => {
    const newDept = value || '';
    const filteredMunicipalities = filterOptions.allMunicipalities
      .filter((m) => m.department === newDept)
      .map((m) => m.name);
    const newMunicipality =
      filteredMunicipalities.length > 0
        ? filteredMunicipalities.includes(Configuration.defaultLocation.municipality)
          ? Configuration.defaultLocation.municipality
          : filteredMunicipalities[0]
        : '';

    setFilters((prev) => ({
      ...prev,
      department: newDept,
      municipality: newMunicipality,
    }));
  };

  const handleMunicipalityFilter = (value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      municipality: value || '',
    }));
  };

  const fetchProductPriceHistory = async (productName: string) => {
    try {
      setChartLoading(true);

      const product = products.find((p) => p.name === productName);
      if (!product) {
        errorNotification(t('productNotFound'));
        return;
      }

      const queryParams: PriceQueryParams = {
        productRefId: product.refId,
        limit: 10,
        sortBy: 'reportedTime',
        sortOrder: 'DESC',
      };

      if (filters.department && filters.municipality) {
        const locationId = await referenceDataSyncService.getLocationId(
          filters.department,
          filters.municipality
        );
        if (locationId) {
          queryParams.location = locationId;
        }
      }

      const response = await fetcher.get(
        API_ENDPOINTS.PRICE.GET_PUBLIC_PRICES,
        queryParams,
        {},
        ENVIRONMENT_CONFIG.CONSUMER_PRICE_SERVICE
      );

      if (response.data?.data) {
        const priceData = response.data.data;

        if (priceData.length === 0) {
          message.info('No price data available for this product');
          setChartData(null);
          return;
        }

        const categories = priceData.map((item: PriceDataItem) => {
          const timestamp = item.reportedTime || item.createTime || Date.now();
          return dayjs(Number(timestamp)).format('YYYY-MM-DD hh:mm:ss A');
        });
        const prices = priceData.map((item: PriceDataItem) => item.price || 0);

        setChartData({
          categories,
          series: [
            {
              name: t('price'),
              data: prices,
              color: '#3b82f6',
            },
          ],
        });
      } else {
        message.info('No price data available for this product');
        setChartData(null);
      }
    } catch (error) {
      console.error('Failed to fetch price history:', error);
      setChartData(null);
    } finally {
      setChartLoading(false);
    }
  };

  const handleProductClick = (productName: string) => {
    setSelectedProductName(productName);
    setIsModalVisible(true);
    fetchProductPriceHistory(productName);
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
  };

  return (
    <div className="section mx-10" ref={ref}>
      <h1 className="section-title">{t('recentInspections')}</h1>
      <div className="filters flex flex-wrap gap-4 lg:flex-nowrap">
        <Select
          className="min-w-0 flex-1 [&>.ant-select-selector]:!rounded-full [&_.ant-select-selection-item]:!text-sm [&_.ant-select-selection-placeholder]:!text-sm [&_.ant-select-selection-placeholder]:!text-black"
          placeholder={t('filterBasket')}
          allowClear
          value={filters.basketId}
          onChange={handleBasketFilter}
          options={filterOptions.baskets.map((basket) => ({
            label: basket.name,
            value: basket.id,
          }))}
          /* showSearch
          filterOption={(input, option) =>
            normalize(option?.label ?? '')
              .toString()
              .toLowerCase()
              .includes(normalize(input.toLowerCase()))
          } */
          title={t('filterBasket')}
        />
        <Select
          className="min-w-0 flex-1 [&>.ant-select-selector]:!rounded-full [&_.ant-select-selection-item]:!text-sm [&_.ant-select-selection-placeholder]:!text-sm [&_.ant-select-selection-placeholder]:!text-black"
          placeholder={t('filterDepartment')}
          allowClear
          value={filters.department || undefined}
          onChange={handleDepartmentFilter}
          options={filterOptions.departments.map((dept) => ({
            label: dept,
            value: dept,
          }))}
          title={t('filterDepartment')}
        />
        <Select
          className="min-w-0 flex-1 [&>.ant-select-selector]:!rounded-full [&_.ant-select-selection-item]:!text-sm [&_.ant-select-selection-placeholder]:!text-sm [&_.ant-select-selection-placeholder]:!text-black"
          placeholder={t('filterMunicipality')}
          allowClear
          value={filters.municipality || undefined}
          onChange={handleMunicipalityFilter}
          disabled={!filters.department || filterOptions.municipalities.length === 0}
          options={
            filterOptions.municipalities.length > 0
              ? filterOptions.municipalities.map((muni) => ({
                  label: muni,
                  value: muni,
                }))
              : []
          }
          notFoundContent={filters.department ? t('noMunicipalities') : t('selectDepartmentFirst')}
          title={t('filterMunicipality')}
        />
      </div>
      <ProductsList
        products={products}
        loading={loading}
        onProductClick={handleProductClick}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: handlePaginationChange,
        }}
      />

      <Modal
        title={t('priceHistoryTitle', { productName: selectedProductName })}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={1000}
        destroyOnClose
      >
        {chartLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spin size="large" />
          </div>
        ) : (
          <div>
            {chartData && (
              <div>
                <ChartComponent
                  title=""
                  type={CHART_TYPES.BAR}
                  series={chartData.series}
                  options={{
                    chart: {
                      toolbar: { show: false },
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: (val: number) => `L.${val}`,
                    },
                    xaxis: {
                      categories: chartData.categories,
                      title: {
                        text: t('dateTime'),
                      },
                      labels: {
                        rotate: -45,
                        style: {
                          fontSize: '12px',
                        },
                      },
                    },
                    yaxis: {
                      title: {
                        text: t('priceYaxis'),
                      },
                    },
                    colors: ['#3b82f6'],
                    grid: {
                      borderColor: '#e7e7e7',
                      strokeDashArray: 4,
                    },
                  }}
                  maxHeight={400}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
});

export default MonitoredProducts;
